/**
 * DDX-Plus Phase 2 — merge KB bundle with Tier 2 unverified source extraction (F.4).
 * Human-verified KB always wins; source never becomes Tier 1 here.
 *
 * Merge rules (explicit):
 * 1. Duplicate diagnosis id: KB row wins; queue low-severity duplicate note; do not add a second Tier 2 row.
 * 2. New source-only diagnoses: append as Tier 2 unverified candidates (never verified here).
 * 3. Edge / scoring authority: KB projection scores stay primary; raw source edges stored on bundle for review.
 * 4. Can’t-miss invariant: never remove or downgrade `cantMiss` from the KB bundle here; if source contradicts
 *    a can’t-miss dx, structural detection emits cant_miss_disagreement (always critical).
 * 5. Structural discrepancies: run detectDiscrepancies (KB nodes vs source edges) and append to queuedDiscrepancies.
 */

import { TRUST_TIERS, VERIFICATION_STATUS } from './ddxTemporalTaxonomy.js';
import { detectDiscrepancies } from './ddxDiscrepancyDetector.js';

/**
 * @param {import('./ddxTypes.js').DdxKnowledgeBundle} kbBundle
 * @param {object[]} [kbEdges] - full CC edges (preferred for discrepancy detection)
 * @returns {object[]}
 */
export function buildKbEdgeNodesForDiscrepancies(kbBundle, kbEdges) {
  if (Array.isArray(kbEdges) && kbEdges.length > 0) {
    return kbEdges
      .filter((e) => e && e.featureId && e.diagnosisId)
      .map((e) => ({
        type: 'edge',
        id: e.id,
        diagnosisId: e.diagnosisId,
        featureId: e.featureId,
        edgeStrength: e.edgeStrength,
        temporalBuckets: e.temporalBuckets,
        sensitivity: e.sensitivity,
        specificity: e.specificity,
        demographicModifiers: e.demographicModifiers,
        contextModifiers: e.contextModifiers,
        direction: e.direction,
      }));
  }
  const nodes = [];
  for (const c of kbBundle?.candidates || []) {
    for (const mf of c.matchedFeatures || []) {
      nodes.push({
        type: 'edge',
        id: mf.edgeId,
        diagnosisId: c.diagnosisId,
        featureId: mf.featureId,
        edgeStrength: mf.edgeStrength,
        temporalBuckets: mf.temporalBuckets,
        sensitivity: mf.sensitivity,
        specificity: mf.specificity,
        demographicModifiers: mf.demographicModifiers,
        contextModifiers: mf.contextModifiers,
        direction: mf.direction,
      });
    }
  }
  return nodes;
}

/**
 * @param {import('./ddxTypes.js').DdxKnowledgeBundle} kbBundle
 * @param {object} sourceContent - parsed ddx-source-extraction JSON
 * @param {{ sourceName?: string, sourceUrl?: string }} [provenance]
 * @param {{ kbEdges?: object[], auditTrigger?: string }} [options]
 * @returns {import('./ddxTypes.js').DdxKnowledgeBundle}
 */
export function mergeKbAndSourceContent(kbBundle, sourceContent, provenance = {}, options = {}) {
  if (!kbBundle || typeof kbBundle !== 'object') return kbBundle;
  const out = { ...kbBundle };
  const srcDx = Array.isArray(sourceContent?.diagnoses) ? sourceContent.diagnoses : [];
  const srcEdges = Array.isArray(sourceContent?.edges) ? sourceContent.edges : [];

  const kbCand = [...(out.candidates || [])];
  const kbIds = new Set(kbCand.map((c) => c.diagnosisId));
  const queuedDiscrepancies = [...(out.queuedDiscrepancies || [])];

  const cantMissDiagnosisIds = (out.cantMiss || []).map((c) => c.diagnosisId).filter(Boolean);

  /** @type {import('./ddxTypes.js').ScoredDiagnosis[]} */
  const tier2Added = [];

  // Rule 1 & 2: duplicate vs new source diagnoses
  for (const d of srcDx) {
    const id = d.id || d.diagnosisId || d.snomedCode || `src_${String(d.label || '').slice(0, 40)}`;
    const label = d.label || d.name || id;
    if (kbIds.has(id)) {
      queuedDiscrepancies.push({
        type: 'kb_vs_source_duplicate_dx',
        severity: 'low',
        kbDiagnosisId: id,
        sourceLabel: label,
        note: 'Source proposed diagnosis already in KB; KB retained',
      });
      continue;
    }
    tier2Added.push({
      diagnosisId: id,
      label,
      adjustedScore: typeof d.provisionalScore === 'number' ? d.provisionalScore : 0.15,
      temporalFitScore: 1,
      matchedFeatures: [],
      missingHighSensitivityFeatures: [],
      isCantMiss: false,
      trustTier: TRUST_TIERS.TIER_2,
      verificationStatus: VERIFICATION_STATUS.UNVERIFIED,
      sourceProvisional: true,
      sourceName: provenance.sourceName,
      sourceUrl: provenance.sourceUrl,
    });
    kbIds.add(id);
  }

  out.candidates = [...kbCand, ...tier2Added];
  out.tier2ProvisionalFromSources = tier2Added;
  out.sourceGroundingProvenance = [
    ...(out.sourceGroundingProvenance || []),
    provenance.sourceName && provenance.sourceUrl
      ? `${provenance.sourceName} ${provenance.sourceUrl}`
      : provenance.sourceName || '',
  ].filter(Boolean);

  out.rawSourceEdgesFromGapFill = srcEdges;

  // Rule 4 & 5: structural discrepancies (includes cant_miss_disagreement when source contradicts KB can’t-miss)
  const enrichedSource = {
    ...sourceContent,
    sourceName: provenance.sourceName ?? sourceContent?.sourceName,
    sourceUrl: provenance.sourceUrl ?? sourceContent?.sourceUrl,
    rawExcerpt: sourceContent?.rawExcerpt ?? '',
    extractionConfidence: sourceContent?.extractionConfidence ?? 0.5,
  };

  const kbNodes = buildKbEdgeNodesForDiscrepancies(out, options.kbEdges);
  const structural = detectDiscrepancies({
    existingKbNodes: kbNodes,
    sourceContent: enrichedSource,
    auditTrigger: options.auditTrigger || 'gap_fill',
    cantMissDiagnosisIds,
  });

  for (const d of structural) {
    queuedDiscrepancies.push({
      type: d.discrepancyType,
      severity: d.severity,
      discrepancyType: d.discrepancyType,
      kbContent: d.kbContent,
      sourceContent: d.sourceContent,
      explanation: d.explanation,
      suggestedAction: d.suggestedAction,
      auditTrigger: d.auditTrigger,
    });
  }

  out.queuedDiscrepancies = queuedDiscrepancies;
  return out;
}
